"use client";

import { useEffect, useState } from "react";

/**
 * Props for {@link HintPill}.
 */
export interface HintPillProps {
  /** Hints to cycle through. Must contain at least one entry. */
  hints: readonly string[];
  /** Rotation interval in milliseconds. Defaults to 1700ms. */
  intervalMs?: number;
}

/**
 * Small "pulsing dot + rotating hint" pill used during the analysis phase of
 * the upload modal. Cycles through `hints` at `intervalMs`, with a soft entry
 * animation on each rotation (the `upload-hint-text` keyframe is defined in
 * `globals.css`).
 *
 * Announces changes politely to assistive tech via `aria-live="polite"`.
 */
export function HintPill({ hints, intervalMs = 1700 }: HintPillProps) {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (hints.length <= 1) {
      return;
    }

    const id = window.setInterval(() => {
      setIdx((i) => (i + 1) % hints.length);
    }, intervalMs);

    return () => {
      window.clearInterval(id);
    };
  }, [hints.length, intervalMs]);

  // Guard: if the hints array length changes and idx is out of range.
  const safeIdx = hints.length === 0 ? 0 : idx % hints.length;
  const current = hints[safeIdx] ?? "";

  return (
    <div
      className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full"
      style={{
        backgroundColor: "var(--color-violet-soft)",
        border: "1px solid rgba(139, 92, 246, 0.18)",
      }}
      aria-live="polite"
    >
      <span
        aria-hidden
        className="wc-pulse-dot inline-block rounded-full"
        style={{
          width: 6,
          height: 6,
          backgroundColor: "var(--color-accent-bright)",
          boxShadow: "0 0 6px rgba(167, 139, 250, 0.7)",
          animation: "wc-pulse-dot 1.4s ease-in-out infinite",
        }}
      />
      <span
        key={safeIdx}
        className="upload-hint-text text-[11px] font-medium"
        style={{
          color: "var(--color-text-primary)",
          opacity: 0.8,
          minWidth: 170,
        }}
      >
        {current}
      </span>
    </div>
  );
}
